import React, { useRef } from 'react';
import { Camera, X, Sparkles, AlertTriangle } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { OnboardingFormData, BOUTIQUE_CATEGORIES } from '../../../types/business';
import { cn } from '../../../lib/utils';
import { ensureBrowserCompatibleImage, IMAGE_LIMITS, padImageToAspectRatio } from '../../../lib/imageUtils';

interface StepBusinessIdentityProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

export function StepBusinessIdentity({ formData, updateField, error }: StepBusinessIdentityProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [imageError, setImageError] = React.useState<string | null>(null);
  const [imageWarning, setImageWarning] = React.useState<string | null>(null);

  const applyProcessedImage = (
    processedFile: File,
    fileField: 'logoFile' | 'coverFile',
    previewField: 'logoPreview' | 'coverPreview',
    wasAdjusted: boolean,
    kind: 'logo' | 'cover',
  ) => {
    updateField(fileField, processedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      updateField(previewField, reader.result as string);
    };
    reader.readAsDataURL(processedFile);

    if (wasAdjusted) {
      setImageWarning(
        kind === 'logo'
          ? "This isn't the optimized format for your logo — square (1:1) images work best. We've added padding so nothing gets stretched or cropped. You can continue with this version or upload a different image."
          : "This isn't the optimized format for your cover photo — a wide (~4.2:1) image works best. We've added padding so nothing gets stretched or cropped. You can continue with this version or upload a different image.",
      );
    } else {
      setImageWarning(null);
    }
  };

  const handleImageSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fileField: 'logoFile' | 'coverFile',
    previewField: 'logoPreview' | 'coverPreview'
  ) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setImageError(null);
    setImageWarning(null);

    const isHeic = /\.(heic|heif)$/i.test(raw.name) || raw.type === 'image/heic' || raw.type === 'image/heif';
    if (!raw.type.startsWith('image/') && !isHeic) return;
    if (raw.size > 10 * 1024 * 1024) return;

    const kind = fileField === 'coverFile' ? 'cover' : 'logo';
    const limits = IMAGE_LIMITS[kind];

    let file: File;
    try {
      file = await ensureBrowserCompatibleImage(raw);
      const processed = await padImageToAspectRatio(file, limits.ratio, limits);
      applyProcessedImage(processed.file, fileField, previewField, processed.wasAdjusted, kind);
    } catch {
      setImageError('Could not process this image format. Please use JPEG or PNG.');
    }

    e.target.value = '';
  };

  const toggleCategory = (cat: string) => {
    const current = formData.categories;
    if (current.includes(cat)) {
      updateField('categories', current.filter(c => c !== cat));
    } else if (current.length < 5) {
      updateField('categories', [...current, cat]);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Tell us about your boutique
        </h2>
        <p className="text-body mt-1">
          This is how shoppers will discover and recognize your brand.
        </p>
      </div>

      {/* Cover Image */}
      <div>
        <label className="text-sm font-medium text-heading block mb-1">
          Cover Image <span className="text-body font-normal">(optional)</span>
        </label>
        <p className="text-xs text-body mb-2">Recommended ~4.2:1 (e.g. 1600×380px) — other sizes are padded automatically</p>
        <div
          onClick={() => coverInputRef.current?.click()}
          className={cn(
            'relative w-full h-40 rounded-card border-2 border-dashed cursor-pointer transition-all overflow-hidden',
            formData.coverPreview
              ? 'border-transparent'
              : 'border-gray-200 hover:border-primary/40 bg-gray-50 hover:bg-purple-50/30'
          )}
        >
          {formData.coverPreview ? (
            <>
              <img
                src={formData.coverPreview}
                alt="Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full">
                  Change Cover
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateField('coverFile', null);
                  updateField('coverPreview', '');
                }}
                className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <Camera className="h-8 w-8 text-gray-300 mb-2" />
              <span className="text-sm text-body">Add a cover photo for your boutique page</span>
            </div>
          )}
        </div>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleImageSelect(e, 'coverFile', 'coverPreview')}
        />
      </div>

      {/* Logo */}
      <div>
        <label className="text-sm font-medium text-heading block mb-2">
          Logo <span className="text-body font-normal">(recommended)</span>
        </label>
        <div className="flex items-center gap-4">
          <div
            onClick={() => logoInputRef.current?.click()}
            className={cn(
              'relative w-20 h-20 rounded-full border-2 border-dashed cursor-pointer transition-all overflow-hidden flex-shrink-0',
              formData.logoPreview
                ? 'border-transparent'
                : 'border-gray-200 hover:border-primary/40 bg-gray-50'
            )}
          >
            {formData.logoPreview ? (
              <img
                src={formData.logoPreview}
                alt="Logo"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Camera className="h-6 w-6 text-gray-300" />
              </div>
            )}
          </div>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => logoInputRef.current?.click()}
            >
              {formData.logoPreview ? 'Change Logo' : 'Upload Logo'}
            </Button>
            <p className="text-xs text-body mt-1">PNG, JPG up to 10MB · Recommended square (1:1) — other sizes are padded automatically</p>
          </div>
        </div>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleImageSelect(e, 'logoFile', 'logoPreview')}
        />
      </div>

      {imageWarning && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{imageWarning}</p>
        </div>
      )}

      {imageError && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
          <X className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{imageError}</p>
        </div>
      )}

      {/* Boutique Name */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">
          Boutique Name <span className="text-error">*</span>
        </label>
        <Input
          type="text"
          placeholder="e.g., Maison de Luxe"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          maxLength={60}
        />
        <p className="text-xs text-body">{formData.name.length}/60 characters</p>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">
          Description <span className="text-error">*</span>
        </label>
        <textarea
          placeholder="Tell shoppers what makes your boutique unique..."
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          rows={4}
          maxLength={500}
          className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
        />
        <p className="text-xs text-body">{formData.description.length}/500 characters</p>
      </div>

      {/* Categories */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading">
          Categories <span className="text-error">*</span>
          <span className="text-body font-normal ml-1">(up to 5)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {BOUTIQUE_CATEGORIES.map((cat) => {
            const isSelected = formData.categories.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCategory(cat)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm font-medium transition-all border',
                  isSelected
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-body border-gray-200 hover:border-primary/40 hover:text-heading'
                )}
              >
                {cat}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-body">
          {formData.categories.length} of 5 selected
        </p>
      </div>
    </div>
  );
}
